import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import CloseIcon from '@material-ui/icons/Close';
import ReplayIcon from '@material-ui/icons/Replay';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { KnowledgeChat } from '../KnowledgeBuilder/KnowledgeChat';
import { useKnowledgeChatContext } from '../KnowledgeBuilder/ChatContext';
import { useKnowledgeBuilderColors } from '../../theme/knowledgeBuilderColors';
import {
  BUTTON_SIZE_PX,
  FloatingAssistantButton,
  POSITION_STORAGE_KEY,
  Position,
  clampPosition,
  loadPosition,
} from './FloatingAssistantButton';
import { GlobalAssistantProvider } from './GlobalAssistantProvider';

const PANEL_WIDTH_PX = 380;
const PANEL_HEIGHT_PX = 560;
const PANEL_GAP_PX = 12;
const PANEL_MARGIN_PX = 8;
const HEADER_HEIGHT_PX = 32;

const EXPANDED_STORAGE_KEY = 'rdap-global-assistant:expanded';
const EXPANDED_POSITION_STORAGE_KEY =
  'rdap-global-assistant:expanded-position';

const EXPANDED_PANEL_WIDTH_PX = 620;
const EXPANDED_PANEL_HEIGHT_PX = 700;

// TODO: export as a variable from the KnowledgeChat component, so that we can
// use it here to determine whether the panel can fit above the button.
const GLOBAL_ASSISTANT_SUGGESTED_PROMPTS = [
  'What can you help me with?',
  'I am new, what can I do?',
  'Summarize this page',
  'Find a service in the catalog',
];

function clampPanelPosition(
  position: Position,
  width = PANEL_WIDTH_PX,
  height = PANEL_HEIGHT_PX,
): Position {
  const actualWidth = Math.min(
    width,
    window.innerWidth - PANEL_MARGIN_PX * 2,
  );

  const actualHeight = Math.min(
    height,
    window.innerHeight - PANEL_MARGIN_PX * 2,
  );

  const maxX = window.innerWidth - actualWidth - PANEL_MARGIN_PX;
  const maxY = window.innerHeight - actualHeight - PANEL_MARGIN_PX;

  return {
    x: Math.min(
      Math.max(position.x, PANEL_MARGIN_PX),
      Math.max(maxX, PANEL_MARGIN_PX),
    ),
    y: Math.min(
      Math.max(position.y, PANEL_MARGIN_PX),
      Math.max(maxY, PANEL_MARGIN_PX),
    ),
  };
}

function defaultExpandedPosition(): Position {
  return clampPanelPosition(
    {
      x: (window.innerWidth - EXPANDED_PANEL_WIDTH_PX) / 2,
      y: (window.innerHeight - EXPANDED_PANEL_HEIGHT_PX) / 2,
    },
    EXPANDED_PANEL_WIDTH_PX,
    EXPANDED_PANEL_HEIGHT_PX,
  );
}

function loadExpandedPosition(): Position {
  try {
    const stored = sessionStorage.getItem(EXPANDED_POSITION_STORAGE_KEY);

    if (stored) {
      return clampPanelPosition(
        JSON.parse(stored) as Position,
        EXPANDED_PANEL_WIDTH_PX,
        EXPANDED_PANEL_HEIGHT_PX,
      );
    }
  } catch {
    // Fall through to default position.
  }

  return defaultExpandedPosition();
}

type PanelSide = 'above' | 'below';

function panelSideFor(button: Position): PanelSide {
  const spaceAbove = button.y - PANEL_GAP_PX;

  return spaceAbove >= PANEL_HEIGHT_PX + PANEL_MARGIN_PX
    ? 'above'
    : 'below';
}

function anchoredPanelPosition(
  button: Position,
  side: PanelSide,
): Position {
  const x = button.x + BUTTON_SIZE_PX - PANEL_WIDTH_PX;

  const y =
    side === 'above'
      ? button.y - PANEL_HEIGHT_PX - PANEL_GAP_PX
      : button.y + BUTTON_SIZE_PX + PANEL_GAP_PX;

  return clampPanelPosition({ x, y });
}

function anchorFromPanelPosition(
  panel: Position,
  side: PanelSide,
): Position {
  const x = panel.x + PANEL_WIDTH_PX - BUTTON_SIZE_PX;

  const y =
    side === 'above'
      ? panel.y + PANEL_HEIGHT_PX + PANEL_GAP_PX
      : panel.y - BUTTON_SIZE_PX - PANEL_GAP_PX;

  return clampPosition({ x, y });
}

const ClearSessionButton = () => {
  const { resetSession, messages } = useKnowledgeChatContext();
  const colors = useKnowledgeBuilderColors();

  if (!resetSession) {
    return null;
  }

  return (
    <Tooltip title="Clear conversation">
      <span
        style={{
          position: 'absolute',
          top: 2,
          right: 66,
          zIndex: 1,
        }}
      >
        <IconButton
          aria-label="Clear conversation"
          size="small"
          disabled={messages.length === 0}
          onClick={() => resetSession()}
          style={{
            width: 28,
            height: 28,
            color: colors.text.primary,
            padding: 4,
          }}
        >
          <ReplayIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

interface PanelDragState {
  pointerId: number;
  offsetX: number;
  offsetY: number;
  side: PanelSide;
}

export const GlobalAssistantWidget = () => {
  const [open, setOpen] = useState(false);

  const [expanded, setExpanded] = useState(
    () => sessionStorage.getItem(EXPANDED_STORAGE_KEY) === 'true',
  );

  const [signedIn, setSignedIn] = useState(false);

  const [buttonPosition, setButtonPosition] =
    useState<Position>(loadPosition);

  const [expandedPosition, setExpandedPosition] =
    useState<Position>(loadExpandedPosition);

  const [isDraggingPanel, setIsDraggingPanel] = useState(false);

  const dragState = useRef<PanelDragState>();

  const panelSide = useMemo(
    () => panelSideFor(buttonPosition),
    [buttonPosition],
  );

  const panelPosition = useMemo(() => {
    if (!open) {
      return undefined;
    }

    if (expanded) {
      return expandedPosition;
    }

    return anchoredPanelPosition(buttonPosition, panelSide);
  }, [
    open,
    expanded,
    expandedPosition,
    buttonPosition,
    panelSide,
  ]);

  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const colors = useKnowledgeBuilderColors();

  const iconUrl = configApi.getOptionalString(
    'knowledge.globalAssistant.iconUrl',
  );

  useEffect(() => {
    let cancelled = false;

    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (!cancelled && identity?.userEntityRef) {
          setSignedIn(true);
        }
      })
      .catch(() => {
        // Not signed in yet (e.g. on the sign-in page) — keep the widget hidden.
      });

    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  useEffect(() => {
    const onResize = () => {
      setButtonPosition(current => clampPosition(current));

      setExpandedPosition(current =>
        clampPanelPosition(
          current,
          EXPANDED_PANEL_WIDTH_PX,
          EXPANDED_PANEL_HEIGHT_PX,
        ),
      );
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const openWidget = useCallback(() => {
    setOpen(true);
  }, []);

  const onHeaderPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!panelPosition) {
        return;
      }

      if (event.button !== 0) {
        return;
      }

      dragState.current = {
        pointerId: event.pointerId,
        offsetX: event.clientX - panelPosition.x,
        offsetY: event.clientY - panelPosition.y,
        side: panelSide,
      };

      setIsDraggingPanel(true);

      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [panelPosition, panelSide],
  );

  const onHeaderPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragState.current;

      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      if (expanded) {
        const nextPosition = clampPanelPosition(
          {
            x: event.clientX - drag.offsetX,
            y: event.clientY - drag.offsetY,
          },
          EXPANDED_PANEL_WIDTH_PX,
          EXPANDED_PANEL_HEIGHT_PX,
        );

        setExpandedPosition(nextPosition);
        return;
      }

      const nextPanelPosition = clampPanelPosition({
        x: event.clientX - drag.offsetX,
        y: event.clientY - drag.offsetY,
      });

      setButtonPosition(
        anchorFromPanelPosition(nextPanelPosition, drag.side),
      );
    },
    [expanded],
  );

  const onHeaderPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragState.current;

      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      dragState.current = undefined;
      setIsDraggingPanel(false);

      if (expanded) {
        setExpandedPosition(current => {
          sessionStorage.setItem(
            EXPANDED_POSITION_STORAGE_KEY,
            JSON.stringify(current),
          );

          return current;
        });

        return;
      }

      setButtonPosition(current => {
        localStorage.setItem(
          POSITION_STORAGE_KEY,
          JSON.stringify(current),
        );

        return current;
      });
    },
    [expanded],
  );

  const onHeaderPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragState.current;

      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      dragState.current = undefined;
      setIsDraggingPanel(false);
    },
    [],
  );

  const panelStyle = useMemo(() => {
    if (!panelPosition) {
      return undefined;
    }

    return {
      position: 'fixed' as const,
      left: panelPosition.x,
      top: panelPosition.y,

      width: expanded
        ? `min(${EXPANDED_PANEL_WIDTH_PX}px, calc(100vw - ${
            PANEL_MARGIN_PX * 2
          }px))`
        : PANEL_WIDTH_PX,

      height: expanded
        ? `min(${EXPANDED_PANEL_HEIGHT_PX}px, calc(100vh - ${
            PANEL_MARGIN_PX * 2
          }px))`
        : PANEL_HEIGHT_PX,

      maxWidth: `calc(100vw - ${PANEL_MARGIN_PX * 2}px)`,
      maxHeight: `calc(100vh - ${PANEL_MARGIN_PX * 2}px)`,

      zIndex: 1300,
      borderRadius: 16,
      overflow: 'hidden',
      background: colors.bg.root,

      display: 'flex' as const,
      flexDirection: 'column' as const,
    };
  }, [panelPosition, colors, expanded]);

  const toggleExpanded = useCallback(() => {
    setExpanded(current => {
      const next = !current;

      sessionStorage.setItem(
        EXPANDED_STORAGE_KEY,
        String(next),
      );

      return next;
    });
  }, []);

  if (!signedIn) {
    return null;
  }

  return (
    <>
      {open && panelStyle && (
        <Paper elevation={8} style={panelStyle}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: HEADER_HEIGHT_PX,
              background: colors.bg.panel,
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              paddingTop: HEADER_HEIGHT_PX,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <GlobalAssistantProvider>
              <ClearSessionButton />

              <KnowledgeChat
                suggestedPrompts={GLOBAL_ASSISTANT_SUGGESTED_PROMPTS}
                logoUrl={iconUrl}
              />
            </GlobalAssistantProvider>
          </div>

          <div
            onPointerDown={onHeaderPointerDown}
            onPointerMove={onHeaderPointerMove}
            onPointerUp={onHeaderPointerUp}
            onPointerCancel={onHeaderPointerCancel}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: HEADER_HEIGHT_PX * 2,
              height: HEADER_HEIGHT_PX,
              cursor: isDraggingPanel ? 'grabbing' : 'grab',
              touchAction: 'none',
              userSelect: 'none',
            }}
          />

          <Tooltip
            title={expanded ? 'Riduci chatbot' : 'Allarga chatbot'}
          >
            <IconButton
              aria-label={
                expanded ? 'Riduci chatbot' : 'Allarga chatbot'
              }
              size="small"
              onClick={toggleExpanded}
              style={{
                position: 'absolute',
                top: 2,
                right: 34,
                width: 28,
                height: 28,
                color: colors.text.primary,
                padding: 4,
              }}
            >
              {expanded ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <IconButton
            aria-label="Close assistant"
            size="small"
            onClick={() => setOpen(false)}
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 28,
              height: 28,
              color: colors.text.primary,
              padding: 4,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}

      {!open && (
        <FloatingAssistantButton
          open={false}
          onToggle={openWidget}
          position={buttonPosition}
          onPositionChange={setButtonPosition}
          iconUrl={iconUrl}
        />
      )}
    </>
  );
};